/**
 * Google Drive Source Registration Form
 * Placeholder component for Google Drive source registration
 */

import React, { useState } from 'react';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { registerGDriveSource } from '../../api/sources';

interface GDriveFormProps {
  projectId: string;
  onSuccess: () => void;
  onClose: () => void;
}

export function GDriveForm({ projectId, onSuccess, onClose }: GDriveFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gdriveName, setGdriveName] = useState('');
  const [gdriveDescription, setGdriveDescription] = useState('');
  const [gdriveServiceAccountJson, setGdriveServiceAccountJson] = useState('');
  const [gdriveSharedDriveId, setGdriveSharedDriveId] = useState('');
  const [gdriveFolderId, setGdriveFolderId] = useState('');
  const [gdriveSyncFrequency, setGdriveSyncFrequency] = useState<'hourly' | '6hourly' | 'daily'>('hourly');
  const [gdriveIncludeShared, setGdriveIncludeShared] = useState(false);
  
  const handleSubmit = async () => {
    setError(null);
    setLoading(true);
    
    try {
      if (!gdriveName || !gdriveServiceAccountJson) {
        setError('Name and service account JSON are required');
        setLoading(false);
        return;
      }
      
      await registerGDriveSource({
        project_id: projectId,
        name: gdriveName,
        description: gdriveDescription || undefined,
        credentials: {
          service_account_json: gdriveServiceAccountJson,
          shared_drive_id: gdriveSharedDriveId || undefined,
        },
        folder_id: gdriveFolderId || undefined,
        watch_for_changes: true,
        recursive: true,
        sync_frequency: gdriveSyncFrequency,
        include_shared_with_me: gdriveIncludeShared,
      });
      
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to register Google Drive source');
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-900">
          Connect to Google Drive to sync documents.
          Requires a Google Cloud service account with Drive API enabled.
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Source Name *
        </label>
        <Input
          value={gdriveName}
          onChange={(e) => setGdriveName(e.target.value)}
          placeholder="e.g., Google Drive Documents, Team Drive"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Service Account JSON *
        </label>
        <textarea
          value={gdriveServiceAccountJson}
          onChange={(e) => setGdriveServiceAccountJson(e.target.value)}
          placeholder='Paste service account JSON credentials here...'
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs"
        />
        <p className="text-xs text-gray-500 mt-1">
          JSON credentials from Google Cloud Console service account
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Shared Drive ID (optional)
        </label>
        <Input
          value={gdriveSharedDriveId}
          onChange={(e) => setGdriveSharedDriveId(e.target.value)}
          placeholder="ID of shared drive (Team Drive) if syncing a shared drive"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Folder ID (optional)
        </label>
        <Input
          value={gdriveFolderId}
          onChange={(e) => setGdriveFolderId(e.target.value)}
          placeholder="Specific folder ID to sync (leave empty for entire Drive)"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sync Frequency
        </label>
        <select
          value={gdriveSyncFrequency}
          onChange={(e) => setGdriveSyncFrequency(e.target.value as any)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="hourly">Hourly</option>
          <option value="6hourly">Every 6 Hours</option>
          <option value="daily">Daily</option>
        </select>
      </div>
      
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="gdrive-shared"
          checked={gdriveIncludeShared}
          onChange={(e) => setGdriveIncludeShared(e.target.checked)}
          className="rounded"
        />
        <label htmlFor="gdrive-shared" className="text-sm text-gray-700">
          Include files shared with service account
        </label>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <Input
          value={gdriveDescription}
          onChange={(e) => setGdriveDescription(e.target.value)}
          placeholder="Optional description"
        />
      </div>
      
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      
      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Registering...' : 'Register Source'}
        </Button>
      </div>
    </div>
  );
}













